import { useState, type FormEvent } from "react";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/base/Breadcrumb";

const FORM_ENDPOINT = "https://readdy.ai/api/form/db74hin799gd4v2febq0";

const contactInfo = [
  { icon: "ri-mail-line", label: "邮箱", value: "hello@chigua-demo.com" },
  { icon: "ri-wechat-line", label: "微信公众号", value: "吃瓜网站（chigua_daily）" },
  { icon: "ri-weibo-line", label: "官方微博", value: "@吃瓜网站官方" },
  { icon: "ri-time-line", label: "工作时间", value: "周一至周五 10:00 - 19:00" },
];

export default function ContactPage() {
  usePageMeta({
    title: "联系我们 - 吃瓜网站",
    description:
      "通过留言表单或社交账号联系吃瓜网站编辑团队，欢迎提供内容建议、合作洽谈与问题反馈，我们会尽快回复你的消息。",
    keywords: "联系吃瓜网站,留言反馈,娱乐资讯合作,内容建议",
  });

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      // 机器人：静默返回与成功一致的反馈
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const message = String(formData.get("message") || "").trim();

    if (!name || !email || !message) {
      setStatus("error");
      setFormError("请完整填写姓名、邮箱和留言内容后再提交。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setFormError("请输入有效的邮箱地址。");
      return;
    }

    setStatus("submitting");
    setFormError("");

    const payload = new URLSearchParams();
    payload.append("name", name);
    payload.append("email", email);
    payload.append("message", message);

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg && !isSpam ? serverMsg : "提交失败，请稍后重试或通过邮箱联系我们。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请检查网络后重试。");
    }
  };

  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-8">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "联系我们" }]} />

      <div className="mt-6">
        <h1 className="text-2xl md:text-3xl font-extrabold text-foreground-950">联系我们</h1>
        <p className="mt-2 text-sm text-foreground-500 max-w-2xl">
          对吃瓜网站有任何建议、合作意向或内容反馈，都可以通过下面的方式找到我们，也可以在留言表单中直接给我们留言。
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-10">
        <div className="lg:col-span-2 space-y-4">
          {contactInfo.map((c) => (
            <div key={c.label} className="flex items-start gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="w-11 h-11 shrink-0 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                <i className={`${c.icon} text-xl`} />
              </span>
              <div>
                <p className="text-xs text-foreground-400">{c.label}</p>
                <p className="mt-0.5 text-sm md:text-base font-medium text-foreground-900 break-all">{c.value}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-3">
          <form
            id="contact-form"
            data-readdy-form
            onSubmit={handleSubmit}
            className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
          >
            <h2 className="text-lg font-bold text-foreground-950 mb-1">给我们留言</h2>
            <p className="text-xs md:text-sm text-foreground-500 mb-5">带 * 的为必填项，我们会在收到后尽快回复。</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  姓名 <span className="text-accent-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  maxLength={40}
                  placeholder="请输入你的称呼"
                  className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
                />
              </div>
              <div>
                <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  邮箱 <span className="text-accent-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder="请输入你的邮箱"
                  className="w-full h-11 px-3 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300"
                />
              </div>
            </div>

            <div className="mt-4">
              <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                留言内容 <span className="text-accent-500">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="请输入你的建议或想说的话（最多 500 字）"
                className="w-full px-3 py-2.5 rounded-md bg-background-100 border border-background-200 text-sm text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-primary-300 resize-y"
              />
              <p className="mt-1 text-xs text-foreground-400">最多 500 字</p>
            </div>

            <div className="form-hp-field" aria-hidden="true">
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
            </div>

            {status === "success" && (
              <div className="mt-4 rounded-md bg-primary-100 border border-primary-200 px-4 py-3 text-sm text-primary-800 flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-lg" />
                反馈已收到，感谢您的留言！
              </div>
            )}
            {status === "error" && formError && (
              <div className="mt-4 rounded-md bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-800 flex items-center gap-2">
                <i className="ri-error-warning-line text-lg" />
                {formError}
              </div>
            )}

            <button
              type="submit"
              disabled={status === "submitting"}
              className="mt-5 w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap transition-colors"
            >
              <i className="ri-send-plane-line" />
              {status === "submitting" ? "提交中..." : "提交留言"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}